import type { Pluscodes } from '~/core/entities/api/ApiPluscodes';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/properties/index.get';

import {
	usePropertyFilters,
	usePropertySearchScope,
	usePropertySearchStateKey
} from '~/composables/propertySearch/context';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

export interface PropertiesListingMapState {
	fetchedData: NuxtListPropertiesApiReturn | null;
	filters: PropertyFilters;
	pluscodes: Pluscodes;
	mapConfig: {
		center: google.maps.LatLngLiteral | google.maps.LatLng;
		zoom?: number | undefined;
		maxZoom: number;
		minZoom: number;
		bounds: google.maps.LatLngBounds[] | google.maps.GeocoderGeometry['bounds'];
	};
}

export interface UseMapListingParams {
	filters: PropertyFilters;
}

export function initializeMapState(): PropertiesListingMapState {
	return {
		fetchedData: null,
		filters: {
			transactionType: 'sell',
			countPluscodes: [],
			propertyTypes: [],
			locationSlug: 'sao-paulo-sp-brasil'
		},
		pluscodes: {},
		mapConfig: {
			zoom: 14,
			maxZoom: 17,
			minZoom: 10,
			bounds: [],
			center: {
				lat: -23.585,
				lng: -46.685
			}
		}
	};
}

export function useMapListing(params: UseMapListingParams) {
	const propertyFiltersApi = usePropertyFilters();
	const scope = usePropertySearchScope();
	let requestRevision = 0;
	let disposed = false;
	onScopeDispose(() => {
		disposed = true;
		requestRevision++;
	});
	const { user } = useUserSession();

	const mapState = useState<PropertiesListingMapState>(
		usePropertySearchStateKey('properties-listing-map'),
		() => initializeMapState()
	);

	const propertiesMapDataFetcher = useAsyncData(
		usePropertySearchStateKey('properties-listing-map'),
		async () => {
			const revision = ++requestRevision;
			try {
				const mapStateFilters = propertyFiltersApi.prepareFiltersForRequest(
					mapState.value.filters,
					{ includeH3Clusters: true }
				);

				const { h3Ids, ...mapFiltersWithoutH3Ids } = mapStateFilters;

				const countPluscodes =
					(scope
						? mapFiltersWithoutH3Ids.countPluscodes?.slice(0, 240)
						: mapFiltersWithoutH3Ids.countPluscodes?.splice(0, 240)) || [];

				const [result, error] = await nuxtPropertiesRepository.list({
					page: 1,
					perPage: 1,
					...params.filters,
					...mapFiltersWithoutH3Ids,
					countPluscodes,
					pluscodesGroup: [],
					includeH3Clusters: true,
					markUserWishlistsId: user.value?.id
				});

				if (scope && (disposed || revision !== requestRevision)) return null;
				if (error) {
					console.error(error);
					mapState.value.fetchedData = null;
					return null;
				}

				mapState.value.fetchedData = result;

				if (result.pluscodes) {
					mapState.value.pluscodes = result.pluscodes;
				}

				return result;
			} catch (error) {
				if (scope && (disposed || revision !== requestRevision)) return null;
				console.error(error);
				mapState.value.fetchedData = null;
				return null;
			}
		},
		{ immediate: false }
	);

	async function updateFilters(newFilters: PropertyFilters) {
		mapState.value.filters = newFilters;

		await propertiesMapDataFetcher.execute();
	}

	return { propertiesMapDataFetcher, updateFilters };
}
